import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogIn, LogOut, User } from "lucide-react";
import { useState } from "react";
import { LOGO_URL } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/magazine", label: "Magazine" },
  { to: "/community", label: "Community" },
  { to: "/feed", label: "Feed" },
  { to: "/directory", label: "Directory" },
  { to: "/film", label: "Film" },
  { to: "/music-videos", label: "Music Videos" },
  { to: "/radio", label: "Radio" },
  { to: "/casino", label: "Casino" },
  { to: "/giveaway", label: "Giveaway" },
  { to: "/marketplace", label: "Shop" },
  { to: "/live", label: "Live" },
  { to: "/jobs", label: "Jobs" },
  { to: "/services", label: "Services" },
  { to: "/events", label: "Events" },
  { to: "/press", label: "Press" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-ink-900/80 backdrop-blur-xl border-b border-white/10" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO_URL} alt="PFC" className="h-12 w-12 object-contain" />
          <div className="hidden sm:block leading-none whitespace-nowrap">
            <div className="font-display text-xl text-white tracking-tight">PUBLIC FIGURES</div>
            <div className="font-sub text-[10px] tracking-[0.3em] text-pfc-gold">CAMPAIGN · EST 2026</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 flex-wrap" data-testid="main-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `font-sub uppercase text-[13px] tracking-[0.2em] transition-colors ${
                  isActive ? "text-pfc-gold" : "text-white/70 hover:text-white"
                }`
              }
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/submit"
            className="font-sub uppercase text-[12px] tracking-[0.2em] px-4 py-2 border border-white/20 text-white hover:bg-white hover:text-ink-900 transition"
            data-testid="header-submit-btn"
          >
            Submit Story
          </Link>
          {user ? (
            <div className="flex items-center gap-2">
              <Link
                to="/dashboard"
                className="font-sub uppercase text-[12px] tracking-[0.2em] px-4 py-2 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition flex items-center gap-1.5"
                data-testid="header-dashboard-btn"
              >
                <User size={14} /> {user.name?.split(" ")[0] || "Account"}
              </Link>
              <button
                onClick={async () => { await logout(); nav("/"); }}
                className="p-2 text-white/60 hover:text-pfc-red transition"
                title="Logout"
                data-testid="header-logout-btn"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="font-sub uppercase text-[12px] tracking-[0.2em] px-4 py-2 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition flex items-center gap-1.5"
              data-testid="header-login-btn"
            >
              <LogIn size={14} /> Sign In
            </Link>
          )}
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-ink-900 border-t border-white/10 px-5 py-6 space-y-4 max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block font-sub uppercase tracking-[0.2em] text-sm ${isActive ? "text-pfc-gold" : "text-white/80"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
            <Link to="/submit" onClick={() => setOpen(false)} className="font-sub uppercase tracking-[0.2em] text-sm text-white">Submit Story</Link>
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="font-sub uppercase tracking-[0.2em] text-sm text-pfc-gold">Dashboard</Link>
                <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="text-left font-sub uppercase tracking-[0.2em] text-sm text-pfc-red">Logout</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="font-sub uppercase tracking-[0.2em] text-sm text-pfc-gold">Sign In</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
